<script>
    import Copy from "@/public/components/common/copy.vue";
    import vClickOutside from "v-click-outside";
    import moment from "moment/moment";
    import BigNumber from "bignumber.js";
    import CurrencyNetworkIcon from "@/public/components/elements/business-walet/Select/CurrencyNetworkIcon.vue";
    import {mapActions, mapGetters, mapState} from "vuex";
    import {getCurrencyInfoById, getCurrencyTicker, getNetworkSystemName, getPrettyAddress} from "@/public/filters";
    import BaseButton from "@/public/components/common/uiKit/button/base/index.vue";
    import CollectingSettingsActions from "@/public/components/modals/collecting-profits/CollectingSettingsActions.vue";
    import EditButton from "@/public/components/common/uiKit/edit-button/index.vue";
    import DeleteButton from "@/public/components/common/uiKit/delete-button/index.vue";

    export default {
        name: "ListMobileItem",
        rulesAlias: "collecting",
        components: {DeleteButton, EditButton, CollectingSettingsActions, BaseButton, CurrencyNetworkIcon, Copy},
        directives: {
            clickOutside: vClickOutside.directive
        },
        props: {
            item: {
                type: Object,
                default: () => ({})
            }
        },
        data () {
            return {
                isOpened: false,
            }
        },
        computed: {
            ...mapState('app', ['networks', 'currencies']),
            ...mapState(['themeStatus']),
            ...mapGetters('organizations', ['userRoleRules']),
            ...mapState('user', {
                userOtpEnabled: state => state.user.otpToken,
            }),
            isActionsAllowedShow () {
                return this.userRoleRules[this.$options.rulesAlias].entities.manage_settings
            },
            isHistoryAllowedShow () {
                return this.userRoleRules[this.$options.rulesAlias].entities.view_history
            },
            network () {
                return this.networks.find(({ id }) => id === this.item.networkId)
            },
            currency () {
                return this.currencies.find(({ id }) => id === this.item.currencyId)
            },
            moment() {
                return moment
            },
            amount () {
                return new BigNumber(this.item.amount)
                    .toFixed()
            },
        },
        methods: {
            getCurrencyInfoById,
            getNetworkSystemName,
            getCurrencyTicker,
            ...mapActions('app', ['openModal']),
            toggleOpen() {
                this.isOpened = !this.isOpened
            },
            close () {
                this.isOpened = false
            },
            goToDetails () {
                this.$router.push(`/collecting-info/${ this.item.id }`)
            },
            goToHistory () {
                if (!this.isHistoryAllowedShow) {
                    return
                }
                this.$router.push(`/collecting-history/${this.item.id}`)
            },
            handleActionClick (type) {
                if (type === 'history' && this.isHistoryAllowedShow) {
                    this.$router.push(`/collecting-history/${this.item.id}`)
                    return;
                }
                if (!this.userOtpEnabled) {
                    this.openModal({
                        name: 'TwoFaRestriction',
                    });
                    return;
                }
                if (!this.isActionsAllowedShow) {
                    return
                }
                if  (type === 'delete') {
                    const payload = {
                        ...this.item
                    }
                    this.openModal({
                        name: 'DeleteSettingModal',
                        payload
                    })
                } else if (type === 'edit') {
                    this.openModal({
                        name: 'UpdateSettingsModal',
                        payload: { ...this.item }
                    })
                }
            },
            walletType (type) {
                if (!type) return ''
                return type === `PAY_IN` ? 'pay-in' : this.$t('profits.personal-wallet-lowercase')
            },
        }
    }
</script>

<template>
<div class="collecting__item" :class="['collecting__item', isOpened ? 'collecting__item--opened' : '']" v-click-outside="close">
    <div class="collecting__item-header" @click="toggleOpen">
        <div class="collecting__item-left">
            <div class="item__asset create-business-modal__padding-wrapper">
                <currency-network-icon :currency="getCurrencyInfoById(item.currencyId)?.currency " :network="getNetworkSystemName(item.networkId).toUpperCase()" />
                <div class="item__asset-currency">
                    {{ getCurrencyTicker(item.currencyId) }}
                </div>
                <div class="item__asset-network">
                    {{ getNetworkSystemName(item.networkId) | getNetworkShortNameByNetworkName }}
                </div>
            </div>
            <div class="collecting__item-desc">
                <div class="collecting__item-amount">{{ $t('profits.amount-to-collect') }}:</div>
                <div class="collecting__item-date">{{ item.amount }}</div>
            </div>
        </div>
        <div class="collecting__item-right">
            <svg :class="['collecting__item-chevron', isOpened ? 'collecting__item-chevron--active' : '']" width="9" height="7" viewBox="0 0 9 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M0.712375 4.38453C0.569375 4.55753 0.484375 4.77853 0.484375 5.01953C0.484375 5.57153 0.932375 6.01953 1.48438 6.01953H7.48438C8.03638 6.01953 8.48438 5.57153 8.48438 5.01953C8.48438 4.77853 8.39937 4.55753 8.25637 4.38453L5.29238 0.430531C5.11037 0.181531 4.81637 0.0195312 4.48438 0.0195312C4.15237 0.0195312 3.85837 0.181531 3.67637 0.430531L0.712375 4.38453Z" fill="var(--new-front-text-primary-bg-primary)"/>
            </svg>
        </div>
    </div>
    <div class="collecting__item-content">
        <div class="collecting__item-element">
            <div class="collecting__item-title">
                {{ $t('profits.wallet') }}:
            </div>
            <div class="collecting__item-value collecting__item-value--black">
                <span>{{ walletType(item.type) }}</span>
            </div>
        </div>
        <div class="collecting__item-element">
            <div class="collecting__item-title">
                {{ $t('profits.collected') }}:
            </div>
            <div class="collecting__item-value collecting__item-value--black">
                <span>{{ item.amountCollected }}</span>
            </div>
        </div>
        <div class="collecting__item-element">
            <div class="collecting__item-title">
                {{ $t('profits.address') }}:
            </div>
            <div class="collecting__item-value">
                <template v-if="item.addressTo">
                <span class="address-underline">{{ item.addressTo | getPrettyAddress }}</span>
                <copy v-if="item.addressTo" :value="item.addressTo"/>
                </template>
            </div>
        </div>
        <div class="listItem__footer">
            <base-button :disabled="!isHistoryAllowedShow" @click="goToHistory" class="history-mobileBtn" type="secondary">
                <template #prepend>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.99996 1.14258C6.24206 1.14759 4.55321 1.82755 3.28225 3.04201V1.82829C3.28225 1.64643 3.21001 1.47202 3.08141 1.34342C2.95281 1.21482 2.7784 1.14258 2.59654 1.14258C2.41467 1.14258 2.24026 1.21482 2.11166 1.34342C1.98307 1.47202 1.91082 1.64643 1.91082 1.82829V4.91401C1.91082 5.09587 1.98307 5.27028 2.11166 5.39888C2.24026 5.52748 2.41467 5.59972 2.59654 5.59972H5.68225C5.86411 5.59972 6.03853 5.52748 6.16712 5.39888C6.29572 5.27028 6.36796 5.09587 6.36796 4.91401C6.36796 4.73214 6.29572 4.55773 6.16712 4.42913C6.03853 4.30054 5.86411 4.22829 5.68225 4.22829H4.03654C4.91821 3.30694 6.09334 2.72154 7.35987 2.57275C8.62641 2.42396 9.90521 2.72107 10.9764 3.41301C12.0476 4.10494 12.8443 5.14846 13.2294 6.36415C13.6146 7.57984 13.5642 8.89173 13.0869 10.0743C12.6096 11.2568 11.7352 12.2361 10.614 12.8438C9.49287 13.4515 8.19504 13.6496 6.94366 13.4041C5.69228 13.1585 4.56554 12.4847 3.75717 11.4984C2.9488 10.5121 2.5093 9.27495 2.51425 7.99972C2.51425 7.81786 2.44201 7.64344 2.31341 7.51485C2.18481 7.38625 2.0104 7.31401 1.82854 7.31401C1.64667 7.31401 1.47226 7.38625 1.34366 7.51485C1.21507 7.64344 1.14282 7.81786 1.14282 7.99972C1.14282 9.35593 1.54499 10.6817 2.29846 11.8093C3.05193 12.937 4.12287 13.8159 5.37585 14.3349C6.62883 14.8539 8.00757 14.9897 9.33773 14.7251C10.6679 14.4605 11.8897 13.8074 12.8487 12.8485C13.8077 11.8895 14.4608 10.6676 14.7253 9.33748C14.9899 8.00733 14.8541 6.62858 14.3351 5.3756C13.8161 4.12263 12.9372 3.05169 11.8096 2.29821C10.6819 1.54474 9.35618 1.14258 7.99996 1.14258ZM7.99996 5.25686C7.8181 5.25686 7.64369 5.32911 7.51509 5.4577C7.38649 5.5863 7.31425 5.76071 7.31425 5.94258V7.99972C7.31425 8.18158 7.38649 8.356 7.51509 8.48459C7.64369 8.61319 7.8181 8.68543 7.99996 8.68543H9.37139C9.55325 8.68543 9.72767 8.61319 9.85627 8.48459C9.98486 8.356 10.0571 8.18158 10.0571 7.99972C10.0571 7.81786 9.98486 7.64344 9.85627 7.51485C9.72767 7.38625 9.55325 7.31401 9.37139 7.31401H8.68568V5.94258C8.68568 5.76071 8.61343 5.5863 8.48484 5.4577C8.35624 5.32911 8.18183 5.25686 7.99996 5.25686Z" :fill="!themeStatus ? '#6750A4' : '#FFFFFF'"/>
                    </svg>
                </template>
                <template #label>
                    <span class="history-mobileBtn--label">{{ $t('profits.history') }}</span>
                </template>
            </base-button>
            <edit-button :disabled="!isActionsAllowedShow" @click="handleActionClick('edit')" />
            <delete-button :disabled="!isActionsAllowedShow" @click="handleActionClick('delete')" />
        </div>
    </div>
</div>
</template>
<style scoped lang="scss">
.collecting__item {
    max-height: 85px;
    transition: max-height .25s ease;
    transition-delay: 50ms;
    overflow: hidden;
    width: 100%;
    border-radius: 10px;
    border: 1px solid var(--new-front-divider-color);
    &-linkBtn {
        margin: 10px 0;
    }
    &-desc {
        display: flex;
        align-items: center;
        gap: 5px;
    }
    &-amount {
        color: var(--new-front-secondary-font-color);
        font-size: $h6;
        font-weight: 500;
    }
    &-date {
        color: var(--new-front-primary-font-color);
        font-size: $h6;
        font-weight: 500;
    }
    &--opened {
        max-height: 320px;
        .collecting__item-header {
            border-bottom: 1px solid var(--new-front-divider-color);
        }
    }
    &-chevron {
        transform: rotate(180deg);
        transition: transform .3s ease;
        &--active {
            transform: rotate(0deg);
        }
    }
    &__asset {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        &-currency {
            font-weight: 500;
            font-size: $h4;
            color: var(--new-front-primary-font-color);
            margin: 0 10px;
        }
        &-network {
            font-weight: 500;
            font-size: $h4;
            color: var(--new-front-secondary-font-color);
        }
    }
  &-title {
    font-size: $h6;
    color: var(--new-front-primary-font-color);
  }
  &-content {
    padding: 0 $p20;
    border-bottom: 1px solid var(--new-front-divider-color);
  }
  &-element {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 50px;
    border-top: 1px solid var(--new-front-divider-color);
      &:first-child {
          border-top: none;
      }
  }
  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
      padding: $p10 $p30 5px $p20;
      min-height: 85px;
      border-bottom: 0 solid var(--new-front-divider-color);
      transition: border-bottom 0.3s ease-in;
  }
  &-right {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  &-status {
    width: 3px;
    border-radius: 300px;
    background: #44B070;
    height: 55px;
      &--error {
          background: #CF5252;
      }
      &--pending {
          background: #BE9B40;
      }
      &--processed {
          background: #44B070;
      }
  }
  &-left {
    display: flex;
    align-items: center;
      flex-direction: column;
    gap: 10px;
  }
  &-value-status {
    width: 10px;
    height: 10px;
    border-radius:2px;
    background: #44B070;
      &--error {
          background: #CF5252;
      }
      &--pending {
          background: #BE9B40;
      }
      &--processed {
          background: #44B070;
      }
  }
  &-value {
    display: flex;
    align-items: center;
    gap: 5px;
    color: var(--new-front-purple);
    font-size: $h6;
      &--black {
          color: var(--new-front-primary-font-color) !important;
      }
      &--error {
          background: #CF5252;
      }
      &--pending {
          background: #BE9B40;
      }
      &--processed {
          background: #44B070;
      }
  }
}
.item {
    &__asset {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        &-currency {
            font-weight: 500;
            font-size: $h4;
            color: var(--new-front-primary-font-color);
            margin: 0 10px;
        }
        &-network {
            font-weight: 500;
            font-size: $h4;
            color: var(--new-front-secondary-font-color);
        }
    }
}
.listItem__footer {
    display: grid;
    grid-template-columns: 1fr 50px 50px;
    gap: 10px;
    margin-top: 10px;
    margin-bottom: 20px;
    .base-btn {
        background: transparent !important;
        border: 1px solid #744DB2 !important;
        transition: background .2s ease !important;
        &:hover {
            background: var(--new-front-bg-btn-secondary-bg-modal-primary-hover) !important;
        }
    }
}
.history-mobileBtn--label {
    margin-left: 10px;
    color: var(--new-front-purple);
}
.address-underline {
    color: var(--new-front-purple);
    text-decoration: underline;
}
::v-deep .base-btn {
    width: 100% !important;
}
</style>
